import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { useQueryLoader, usePreloadedQuery, PreloadedQuery } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { MemberModalMemberList } from "./MemberModalMemberList";
import { MemberModalSearchBar } from "./MemberModalSearchBar";
import { MemberModalContentQuery } from "./__generated__/MemberModalContentQuery.graphql";

const MemberModalContentInner = ({
  queryRef,
  isOpen,
}: {
  queryRef: PreloadedQuery<MemberModalContentQuery>;
  isOpen: boolean;
}) => {
  const data = usePreloadedQuery(MemberModalContentQueryString, queryRef);

  return data.session?.currentClientProfile?.member?.club ? (
    <MemberModalMemberList
      memberListRef={data.session?.currentClientProfile?.member?.club}
      isOpen={isOpen}
    />
  ) : null;
};

export const MemberModalContent = ({ isOpen }: { isOpen: boolean }) => {
  const [searchVal, setSearchVal] = useState("");
  const [queryRef, loadQuery] = useQueryLoader<MemberModalContentQuery>(
    MemberModalContentQueryString
  );

  /**
   * When the modal closes, we clear search
   */
  useEffect(() => {
    if (isOpen === false) {
      setSearchVal("");
    }
  }, [isOpen]);

  useEffect(() => {
    loadQuery({ search: searchVal, first: 20 });
  }, [loadQuery, searchVal]);

  return (
    <VStack flex={1}>
      <Box px={6}>
        <Text pt={8} pb={4} variant="mainTitle2">
          {t`Members`}
        </Text>
        <Box pb={6}>
          <MemberModalSearchBar onChange={setSearchVal} isOpen={isOpen} />
        </Box>
      </Box>
      <Box flex={1}>
        <Suspense fallback={<Loader />}>
          {queryRef ? (
            <MemberModalContentInner queryRef={queryRef} isOpen={isOpen} />
          ) : null}
        </Suspense>
      </Box>
    </VStack>
  );
};

export const MemberModalContentQueryString = graphql`
  query MemberModalContentQuery($search: String, $first: Int, $after: String) {
    session {
      currentClientProfile {
        member {
          club {
            ...MemberModalMemberListFragment
          }
        }
      }
    }
  }
`;
